/**
 * DynamicEngagementLauncher — a sharp chooser modal shown when the IBCS (or JIATF-401) →
 * Dynamic Engagement nav item finds more than one accepted deployment plan.
 * Pick a plan to launch it into the engagement theater. (Zero plans navigate
 * straight through; a single plan launches without this dialog.)
 */

import React, { useEffect } from 'react';

const C = {
  bg: '#070a0d', panel: '#0c1117', border: '#1a2533', text: '#e6f0ff', dim: '#5a7090', blue: '#3b82f6',
};

const EMPHASIS_ICONS = {
  balanced: '⊕', defense_in_depth: '⇉', counter_swarm: '⚇', economy_of_force: '◈',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function DynamicEngagementLauncher({ plans, onPick, onClose, eyebrow = 'Integrated Air & Missile Defense' }) {
  useEffect(() => {
    if (!plans) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [plans, onClose]);

  if (!plans) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(3,5,8,0.72)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(680px, 100%)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: '0 24px 80px rgba(0,0,0,0.6)', overflow: 'hidden', fontFamily: 'Rajdhani, system-ui, sans-serif' }}
      >
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontFamily: '"Share Tech Mono", monospace', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.blue }}>{eyebrow}</div>
            <div style={{ fontSize: 19, fontWeight: 800, color: C.text, marginTop: 3 }}>Choose a deployment plan to engage</div>
            <div style={{ fontSize: 12.5, color: C.dim, marginTop: 2 }}>{plans.length} accepted plans available — select one to launch dynamic engagement.</div>
          </div>
          <button onClick={onClose} title="Close (Esc)" style={{ border: 'none', background: 'transparent', color: C.dim, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: 14, display: 'grid', gap: 10 }}>
          {plans.map((p) => (
            <button
              key={p.id}
              onClick={() => onPick(p)}
              style={{ textAlign: 'left', cursor: 'pointer', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', color: C.text, display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', transition: 'border-color 0.15s, background 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.background = 'rgba(59,130,246,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.bg; }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>{(p.site_name || p.site_id || 'Site').replace(/\(NOTIONAL\)/i, '').trim()}</div>
                <div style={{ fontSize: 16, fontWeight: 800, margin: '2px 0' }}>{EMPHASIS_ICONS[p.emphasis] || '▢'} {p.name?.replace(/\s*\((Optimized|On-Hand)\)\s*$/i, '')}</div>
                <div style={{ fontSize: 12, color: '#93c5fd' }}>{p.doctrine || '—'}</div>
                <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11.5, color: C.dim, flexWrap: 'wrap' }}>
                  <span><b style={{ color: C.text }}>{p.system_count}</b> systems ({p.sensor_count}S·{p.effector_count}E)</span>
                  <span>leak <b style={{ color: (p.leakage_pct || 0) > 15 ? '#fca5a5' : '#86efac' }}>{p.leakage_pct != null ? `${p.leakage_pct}%` : '—'}</b></span>
                  <span>{p.gap_count} gap{p.gap_count === 1 ? '' : 's'}</span>
                  <span>{fmtDate(p.accepted_at)}</span>
                </div>
              </div>
              <span style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 800, color: '#fff', background: C.blue, borderRadius: 7, padding: '9px 14px', whiteSpace: 'nowrap' }}>🚀 Launch</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
